import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle } from 'lucide-react';

export default function AdminLogin() {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/admin/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(d => d.msg || JSON.stringify(d)).join(' '));
      else setError('Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#F5F5F7] flex items-center justify-center pt-20 px-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-10">
          <h1
            className="text-4xl font-light tracking-tight mb-3"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#D4AF37' }}
          >
            Kirei II
          </h1>
          <p className="text-xs tracking-[0.2em] uppercase text-[#8A828F]" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {t('admin.title')}
          </p>
        </div>

        <div className="bg-white border border-[#E5E5E5] p-8 md:p-10">
          <h2
            className="text-2xl font-light tracking-tight mb-8 text-center"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A1A1A' }}
          >
            {t('admin.login')}
          </h2>

          {error && (
            <div data-testid="login-error" className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-6 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-xs tracking-[0.1em] uppercase text-[#8A828F] mb-2 block">
                {t('admin.email')}
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-b border-t-0 border-l-0 border-r-0 border-[#8A828F] rounded-none bg-transparent px-0 py-2 focus:border-[#D4AF37] focus-visible:ring-0 focus-visible:ring-offset-0"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs tracking-[0.1em] uppercase text-[#8A828F] mb-2 block">
                {t('admin.password')}
              </Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-b border-t-0 border-l-0 border-r-0 border-[#8A828F] rounded-none bg-transparent px-0 py-2 focus:border-[#D4AF37] focus-visible:ring-0 focus-visible:ring-offset-0"
                required
              />
            </div>
            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full bg-[#D4AF37] text-white py-4 uppercase tracking-[0.15em] text-sm font-light hover:bg-[#B5952F] transition-colors duration-300 disabled:opacity-50"
            >
              {loading ? '...' : t('admin.loginBtn')}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
